import { AlertTriangle, RotateCcw } from 'lucide-react';
import { Link, isRouteErrorResponse, useRouteError } from 'react-router-dom';

import { Button } from './ui/Button';
import { Card } from './ui/Card';

/**
 * Was der Benutzer sieht, wenn eine Seite beim Aufbau scheitert.
 *
 * Bis 2026-08-12 gab es das nicht: Ein Fehler in irgendeiner Komponente riss
 * die ganze Anwendung herunter - **weisse Flaeche, kein Text, kein Weg
 * zurueck.** In dieser Sitzung ist genau das zweimal passiert:
 *
 * - Der `Button`-Baustein reichte `asChild` zwei Kindelemente durch; jede
 *   Seite mit `<Button asChild>` war danach leer.
 * - Der Servicekalender las `user.tenants.some(...)` an einem Benutzer ohne
 *   Mandantenliste - das ganze Service-Cockpit war weiss.
 *
 * Beide Male habe ich es nur gesehen, weil zufaellig ein Bildvergleich lief.
 * Im Betrieb heisst das: Seite weiss, Techniker ruft an.
 *
 * Der Text nennt bewusst **keinen** technischen Fehlertext im Klartext - ein
 * SHK-Betrieb kann damit nichts anfangen, und Fehlermeldungen enthalten
 * gelegentlich Pfade oder Kennungen. Fuer die Fehlersuche steht er in der
 * Browserkonsole.
 */

export const Fehlerseite: React.FC<{ imRahmen?: boolean }> = ({ imRahmen = false }) => {
  const fehler = useRouteError();

  // In die Konsole, nicht auf den Bildschirm.
  console.error('Ansicht konnte nicht aufgebaut werden:', fehler);

  const istNichtGefunden = isRouteErrorResponse(fehler) && fehler.status === 404;

  const inhalt = (
    <Card padding="lg" flat className="mx-auto max-w-xl text-center">
      <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-50 text-amber-700">
        <AlertTriangle className="h-6 w-6" />
      </span>
      <h1 className="mt-4 text-xl font-bold text-slate-900">
        {istNichtGefunden ? 'Diese Ansicht gibt es nicht' : 'Diese Ansicht konnte nicht geladen werden'}
      </h1>
      <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">
        {istNichtGefunden
          ? 'Der Link führt ins Leere. Über das Menü kommen Sie zurück.'
          : 'Der Rest der Anwendung läuft weiter. Ein erneuter Versuch hilft oft; bleibt es dabei, hilft der technische Ansprechpartner weiter.'}
      </p>
      <div className="mt-5 flex flex-wrap justify-center gap-2">
        {!istNichtGefunden && (
          <Button onClick={() => window.location.reload()}>
            <RotateCcw className="h-4 w-4" />
            Erneut versuchen
          </Button>
        )}
        <Button tone="secondary" asChild>
          <Link to="/">Zum Service-Cockpit</Link>
        </Button>
      </div>
    </Card>
  );

  // Innerhalb des Rahmens bleibt die Navigation stehen - man kommt woanders
  // hin, ohne die Anwendung neu zu laden.
  if (imRahmen) return inhalt;

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6">
      {inhalt}
    </div>
  );
};

export default Fehlerseite;
